import * as Linking from 'expo-linking';
import { useEffect } from 'react';

import { apiService } from '@/services/api-service';
import { authService } from '@/services/auth-service';
import { devSignInProblem, isDevBuild } from '@/utils/dev-sign-in';

const DEV_SIGN_IN_PATH = 'dev/sign-in';

/** Signs in from a `…/dev/sign-in?token=` URL when every guard passes; anything else is ignored. */
const handleUrl = async (url: string | null): Promise<void> => {
	if (!url || !isDevBuild()) return;
	const { path, queryParams } = Linking.parse(url);
	if (path !== DEV_SIGN_IN_PATH) return;
	const token = queryParams?.token;
	if (devSignInProblem(token, apiService.getApiBaseUrl()) !== null || typeof token !== 'string') return;
	try {
		await authService.signInWithDeviceTokenForDev(token);
	} catch (e) {
		// The message only — never the URL, which carries the token.
		console.warn('[dev sign-in]', (e as Error).message);
	}
};

/**
 * DEV ONLY: the `workorder:///dev/sign-in?token=…` link while SIGNED OUT.
 *
 * Signed out, the root layout renders `<SignIn />` with no navigator mounted, so a link opened
 * into the running app is never routed and `app/dev/sign-in.tsx` never mounts. This listens for
 * the link directly instead, under the same guards (`devSignInProblem`) and through the same
 * `authService.signInWithDeviceTokenForDev`. Off while signed in: the route handles it then.
 *
 * @param signedOut  listen only while there is no session
 */
export const useDevSignInLink = (signedOut: boolean): void => {
	useEffect(() => {
		// Runtime links only. A COLD launch is routed normally (the layout treats `/dev` as
		// public), so the route signs in; handling the launch URL here as well would sign in twice.
		if (!signedOut || !isDevBuild()) return;
		const subscription = Linking.addEventListener('url', ({ url }) => {
			void handleUrl(url);
		});
		return () => subscription.remove();
	}, [signedOut]);
};
